import { useEffect, useState, useCallback } from "react";
import { Plus, X, Trash2, Loader2, ArrowLeft, Save, BookmarkPlus, FolderOpen } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import CaseProgress from "@/components/CaseProgress";

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "on_hold", label: "On Hold" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

// Fixed vocabulary so stage names are consistent across applications — and, critically,
// so the approve/reject workflow's auto stage-update can reliably find "Documents Approved"
// / "Documents Rejected" by exact label match.
const STAGE_OPTIONS = [
  "Awaiting Documents",
  "Documents Submitted",
  "Under Review",
  "Documents Approved",
  "Documents Rejected",
  "Application In Progress",
  "Application Submitted",
  "Completed",
  "Others",
];

function StageListEditor({ stages, setStages }) {
  const [selected, setSelected] = useState("");
  const [customLabel, setCustomLabel] = useState("");

  function addStage() {
    if (!selected) return;
    const v = selected === "Others" ? customLabel.trim() : selected;
    if (!v) return;
    setStages((s) => [...s, v]);
    setSelected("");
    setCustomLabel("");
  }

  function removeStage(idx) {
    setStages((s) => s.filter((_, i) => i !== idx));
  }

  const canAdd = selected && (selected !== "Others" || customLabel.trim());

  return (
    <div>
      {stages.length > 0 && (
        <ol className="space-y-1.5 mb-3">
          {stages.map((s, idx) => (
            <li key={idx} className="flex items-center justify-between bg-white/5 rounded-lg px-3 py-1.5 text-sm text-white/80">
              <span>{idx + 1}. {s}</span>
              <button type="button" onClick={() => removeStage(idx)} className="text-white/40 hover:text-red-400">
                <X size={14} />
              </button>
            </li>
          ))}
        </ol>
      )}
      <div className="flex gap-2">
        <select
          value={selected}
          onChange={(e) => { setSelected(e.target.value); setCustomLabel(""); }}
          className="input-dark"
          data-testid="case-stage-select"
        >
          <option value="" disabled>— Select a stage —</option>
          {STAGE_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
        </select>
        <button type="button" onClick={addStage} disabled={!canAdd} className="btn-outline-gold text-sm py-2 px-4 shrink-0 disabled:opacity-40">
          <Plus size={14} /> Add
        </button>
      </div>
      {selected === "Others" && (
        <input
          value={customLabel}
          onChange={(e) => setCustomLabel(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addStage(); } }}
          placeholder="Custom stage name"
          className="input-dark mt-2"
          data-testid="case-stage-custom"
        />
      )}
    </div>
  );
}

function CreateCaseForm({ templates, onCreated, onCancel }) {
  const [form, setForm] = useState({ client_email: "", title: "", service_type: "", notes: "" });
  const [stages, setStages] = useState([]);
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  function applyTemplate(e) {
    const t = templates.find((t) => t.id === e.target.value);
    if (t) setStages(t.stages);
  }

  async function submit(e) {
    e.preventDefault();
    if (stages.length === 0) {
      toast.error("Add at least one stage");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/admin/cases", { ...form, stages });
      if (saveAsTemplate && templateName.trim()) {
        await api.post("/admin/case-templates", { name: templateName.trim(), stages });
      }
      toast.success("Application created");
      onCreated();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not create application");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="card-dark p-6" data-testid="case-create-form">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-xl font-bold text-white">New Application</h2>
        <button onClick={onCancel} className="text-white/50 hover:text-white text-sm inline-flex items-center gap-1">
          <ArrowLeft size={14} /> Back
        </button>
      </div>
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">Client email</label>
            <input required type="email" value={form.client_email} onChange={upd("client_email")} className="input-dark" data-testid="case-client-email" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">Title</label>
            <input required value={form.title} onChange={upd("title")} placeholder="e.g. Student Visa — Germany" className="input-dark" data-testid="case-title" />
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">Service type (optional)</label>
          <input value={form.service_type} onChange={upd("service_type")} className="input-dark" />
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-xs uppercase tracking-widest text-white/55 block">Stages</label>
            {templates.length > 0 && (
              <select onChange={applyTemplate} defaultValue="" className="input-dark w-auto text-xs py-1.5">
                <option value="" disabled>Use a template...</option>
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            )}
          </div>
          <StageListEditor stages={stages} setStages={setStages} />
        </div>

        <div>
          <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">Notes to client (optional)</label>
          <textarea value={form.notes} onChange={upd("notes")} rows={3} className="input-dark" />
        </div>

        <label className="flex items-center gap-2 text-sm text-white/70">
          <input type="checkbox" checked={saveAsTemplate} onChange={(e) => setSaveAsTemplate(e.target.checked)} />
          Save this stage list as a reusable template
        </label>
        {saveAsTemplate && (
          <input
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder="Template name, e.g. Student Visa"
            className="input-dark"
          />
        )}

        <button type="submit" disabled={submitting} className="btn-gold text-sm py-2 px-5">
          {submitting ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          Create Application
        </button>
      </form>
    </div>
  );
}

function LinkedDocuments({ caseId }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/documents", { params: { case_id: caseId } });
      setDocs(data.items || []);
    } catch {
      // non-critical — section just won't show
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="text-center py-6"><Loader2 size={18} className="text-[var(--gold)] animate-spin mx-auto" /></div>;
  }
  if (docs.length === 0) {
    return <p className="text-sm text-white/45" data-testid="case-linked-documents-empty">No documents linked to this application yet.</p>;
  }

  const STATUS_META = {
    pending: { label: "Pending", className: "border-white/15 text-white/50" },
    approved: { label: "Approved", className: "border-emerald-400/30 text-emerald-400" },
    rejected: { label: "Rejected", className: "border-red-400/30 text-red-400" },
  };

  return (
    <div className="space-y-3" data-testid="case-linked-documents">
      {docs.map((doc) => (
        <div key={doc.id} className="bg-white/5 rounded-lg p-3">
          <div className="flex items-center justify-between text-xs text-white/55 mb-2">
            <span>{doc.name}</span>
            <span>{new Date(doc.created_at).toLocaleDateString()}</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {doc.files.map((f) => {
              const meta = STATUS_META[f.status] || STATUS_META.pending;
              return (
                <span
                  key={f.id}
                  className={`text-xs px-2.5 py-1 rounded-full border ${meta.className}`}
                  title={f.rejection_reason || undefined}
                >
                  {f.label ? `${f.label}: ${f.original_name}` : f.original_name} — {meta.label}
                </span>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function CaseDetail({ caseId, isAdmin, onBack, onChanged }) {
  const [caseItem, setCaseItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("active");
  const [currentStageIndex, setCurrentStageIndex] = useState(0);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/cases/${caseId}`);
      setCaseItem(data);
      setStatus(data.status);
      setCurrentStageIndex(data.current_stage_index);
      setNotes(data.notes || "");
    } catch {
      toast.error("Failed to load application");
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { load(); }, [load]);

  async function save() {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/cases/${caseId}`, {
        status,
        current_stage_index: currentStageIndex,
        notes,
      });
      setCaseItem(data);
      toast.success("Application updated — client notified by email");
      onChanged();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!window.confirm("Delete this application permanently?")) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/cases/${caseId}`);
      toast.success("Deleted");
      onChanged();
      onBack();
    } catch {
      toast.error("Delete failed");
    } finally {
      setDeleting(false);
    }
  }

  if (loading || !caseItem) {
    return <div className="text-center py-12"><Loader2 size={24} className="text-[var(--gold)] animate-spin mx-auto" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="case-detail">
      <button onClick={onBack} className="text-white/50 hover:text-white text-sm inline-flex items-center gap-1">
        <ArrowLeft size={14} /> Back to all applications
      </button>

      <div className="card-dark p-6">
        <p className="text-xs text-white/50 mb-4">{caseItem.client_email}</p>
        <CaseProgress caseItem={{ ...caseItem, status, current_stage_index: currentStageIndex, notes }} />
      </div>

      <div className="card-dark p-6 space-y-4">
        <h3 className="font-display text-lg font-bold text-white">Update Progress</h3>
        <div>
          <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">Current stage</label>
          <div className="flex flex-wrap gap-2">
            {caseItem.stages.map((s, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => setCurrentStageIndex(idx)}
                className={`text-xs px-3 py-1.5 rounded-full border transition ${
                  idx === currentStageIndex
                    ? "border-[var(--gold)] text-[var(--gold)] bg-[var(--gold)]/10"
                    : "border-white/15 text-white/60 hover:border-white/30"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">Status</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="input-dark w-auto">
            {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-widest text-white/55 mb-1.5 block">Notes to client</label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="input-dark" />
        </div>
        <div className="flex items-center gap-3">
          <button onClick={save} disabled={saving} className="btn-gold text-sm py-2 px-5">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save Changes
          </button>
          {isAdmin && (
            <button onClick={remove} disabled={deleting} className="text-sm text-red-400/80 hover:text-red-400 inline-flex items-center gap-1.5">
              {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              Delete
            </button>
          )}
        </div>
      </div>

      <div className="card-dark p-6">
        <h3 className="font-display text-lg font-bold text-white mb-4 flex items-center gap-2">
          <FolderOpen size={17} className="text-[var(--gold)]" /> Linked documents
        </h3>
        <LinkedDocuments caseId={caseId} />
      </div>
    </div>
  );
}

export default function CasesPanel({ isAdmin }) {
  const [view, setView] = useState("list");
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [selectedCaseId, setSelectedCaseId] = useState(null);

  const loadCases = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/cases");
      setCases(data.items || []);
    } catch {
      toast.error("Failed to load applications");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTemplates = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/case-templates");
      setTemplates(data.items || []);
    } catch {
      // non-critical — template picker just won't show options
    }
  }, []);

  useEffect(() => { loadCases(); loadTemplates(); }, [loadCases, loadTemplates]);

  if (view === "create") {
    return (
      <CreateCaseForm
        templates={templates}
        onCancel={() => setView("list")}
        onCreated={() => { setView("list"); loadCases(); }}
      />
    );
  }

  if (view === "detail" && selectedCaseId) {
    return (
      <CaseDetail
        caseId={selectedCaseId}
        isAdmin={isAdmin}
        onBack={() => setView("list")}
        onChanged={loadCases}
      />
    );
  }

  return (
    <div className="space-y-3" data-testid="admin-cases-panel">
      <div className="flex justify-end mb-1">
        <button onClick={() => setView("create")} className="btn-gold text-sm py-2 px-4" data-testid="case-new-button">
          <Plus size={14} /> New Application
        </button>
      </div>
      {loading && <div className="text-center py-12"><Loader2 size={24} className="text-[var(--gold)] animate-spin mx-auto" /></div>}
      {!loading && cases.length === 0 && (
        <div className="card-dark p-12 text-center text-white/55" data-testid="admin-no-cases">
          No applications yet. Create one to start tracking a client's progress.
        </div>
      )}
      {cases.map((c) => (
        <button
          key={c.id}
          onClick={() => { setSelectedCaseId(c.id); setView("detail"); }}
          className="card-dark p-5 w-full text-left hover:border-[var(--gold)]/30 transition"
          data-testid={`case-row-${c.id}`}
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="font-semibold text-white">{c.title}</h3>
              <p className="text-xs text-white/50 mt-0.5">{c.client_email}</p>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <span className="px-2.5 py-1 rounded-full border border-[var(--gold)]/25 text-[var(--gold)]">
                {c.stages[c.current_stage_index]?.label || c.status}
              </span>
              <span className="text-white/40">{new Date(c.updated_at).toLocaleDateString()}</span>
            </div>
          </div>
        </button>
      ))}
      {templates.length > 0 && (
        <div className="pt-4 flex items-center gap-2 text-xs text-white/40">
          <BookmarkPlus size={13} /> {templates.length} saved stage template{templates.length === 1 ? "" : "s"}
        </div>
      )}
    </div>
  );
}
